import React, { useState, useEffect, useCallback } from "react";
import { X, Search, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { searchUsers } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * StakeholderInput Component
 * 
 * A multi-select input component for searching and selecting users to tag as stakeholders.
 * Features:
 * - Debounced user search (300ms)
 * - Multi-select with user chips
 * - Optional role note per selected user (max 100 chars)
 * - Remove functionality for selected users
 * - Loading and error states
 * 
 * @param {Array} value - Array of selected stakeholders with structure: [{ user_id, user_name, user_role, role_note }]
 * @param {Function} onChange - Callback when selection changes, receives updated stakeholders array
 * @param {Boolean} disabled - Whether the input is disabled
 */
const StakeholderInput = ({ value = [], onChange, disabled = false }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [error, setError] = useState(null);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (query) => {
      try {
        setIsSearching(true);
        setError(null);
        const response = await searchUsers(query, 10);
        
        const selectedUserIds = value.map(s => s.user_id);
        const filteredResults = (response.users || []).filter(
          user => !selectedUserIds.includes(user.id)
        );
        
        setSearchResults(filteredResults);
      } catch (err) {
        setError("Failed to search users");
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    [value]
  );

  // Trigger search when query changes
  useEffect(() => {
    debouncedSearch(searchQuery);
  }, [searchQuery, debouncedSearch]);

  // Handle user selection from dropdown
  const handleSelectUser = (user) => {
    const newStakeholder = {
      user_id: user.id,
      user_name: user.full_name,
      user_role: user.role,
      role_note: "",
    };
    
    onChange([...value, newStakeholder]);
    setSearchQuery("");
    setSearchResults([]);
    setShowDropdown(false);
  };

  // Handle removing a selected user
  const handleRemoveUser = (userId) => {
    onChange(value.filter(s => s.user_id !== userId));
  };

  // Handle role note change for a specific user
  const handleRoleNoteChange = (userId, roleNote) => {
    // Enforce 100 character limit
    if (roleNote.length > 100) {
      return;
    }
    
    onChange(
      value.map(s =>
        s.user_id === userId ? { ...s, role_note: roleNote } : s
      )
    );
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        Involved People
      </label>

      {/* Selected Users Chips */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
          {value.map((stakeholder) => (
            <Badge
              key={stakeholder.user_id}
              variant="secondary"
              className="flex items-center gap-1 px-3 py-1.5 text-sm"
            >
              <User size={14} />
              <span>{stakeholder.user_name}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveUser(stakeholder.user_id)}
                  className="ml-1 hover:bg-gray-300 rounded-full p-0.5 transition-colors"
                  aria-label={`Remove ${stakeholder.user_name}`}
                >
                  <X size={14} />
                </button>
              )}
            </Badge>
          ))}
        </div>
      )}

      {/* Search Input with Dropdown */}
      <div className="relative">
        <Command className="rounded-lg border shadow-md">
          <div className="flex items-center border-b px-3">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              placeholder="Search for people..."
              disabled={disabled}
              className="flex h-10 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          {/* Dropdown Results */}
          {showDropdown && (
            <CommandList>
              {isSearching && (
                <div className="py-6 text-center text-sm text-gray-500">
                  Searching...
                </div>
              )}

              {!isSearching && error && (
                <div className="py-6 text-center text-sm text-red-500">
                  {error}
                </div>
              )}

              {!isSearching && !error && searchResults.length === 0 && (
                <CommandEmpty>No users found</CommandEmpty>
              )}

              {!isSearching && !error && searchResults.length > 0 && (
                <CommandGroup>
                  {searchResults.map((user) => (
                    <CommandItem
                      key={user.id}
                      onSelect={() => handleSelectUser(user)}
                      className="cursor-pointer"
                    >
                      <User className="mr-2 h-4 w-4" />
                      <div className="flex flex-col">
                        <span className="font-medium">{user.full_name}</span>
                        <span className="text-xs text-gray-500">
                          {user.role}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          )}
        </Command>
      </div>

      {/* Role Notes for Selected Users */}
      {value.length > 0 && (
        <div className="space-y-3">
          {value.map((stakeholder) => (
            <div key={stakeholder.user_id} className="space-y-1">
              <label className="text-xs font-medium text-gray-600">
                Role for {stakeholder.user_name}
              </label>
              <Input
                type="text"
                value={stakeholder.role_note}
                onChange={(e) =>
                  handleRoleNoteChange(stakeholder.user_id, e.target.value)
                }
                placeholder="e.g., gave pricing approval, coordinated with customer"
                disabled={disabled}
                maxLength={100}
                className="text-sm"
              />
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">
                  Optional - describe their contribution
                </span>
                <span className="text-xs text-gray-400">
                  {stakeholder.role_note.length}/100
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default StakeholderInput;
